import { ReactNode, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Bot, ShieldAlert } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Separator } from "~/components/ui/separator";
import useToast from "~/components/ui/toast";
import useNextGenHeader from "~/contexts/nextgen-header";
import { usePageContext } from "~/contexts/page-context";
import useUser from "~/contexts/user";
import { useDebounce } from "~/hooks/useDebounce";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { usePinnedList } from "~/hooks/usePinnedList";
import { useServerList } from "~/hooks/useServerList";
import { useUserPreferences } from "~/hooks/useUserPreferences";
import { useViewMode } from "~/hooks/useViewMode";
import { NEXTGEN_MAX_CONTENT_WIDTH } from "~/lib/layout";
import { AgentViewFilter } from "~/pages/manifold-nextgen/components/AgentViewFilter";
import { DividedList } from "~/pages/manifold-nextgen/components/DividedList";
import { ListLoadError } from "~/pages/manifold-nextgen/components/ListLoadError";
import { ListLoadingSkeleton } from "~/pages/manifold-nextgen/components/ListLoadingSkeleton";
import { ViewToggle } from "~/pages/manifold-nextgen/components/ViewToggle";
import { useCanAdministerAIAgents } from "~/pages/manifold-nextgen/hooks/useCanAdministerAIAgents";
import { useCanManageAIAgents } from "~/pages/manifold-nextgen/hooks/useCanManageAIAgents";
import { ROUTE_NEXTGEN_VIEW } from "~/router/constants";
import {
  Agent,
  AGENT_FAVORITES_KEY,
  AgentAccessLevel,
  AgentListView,
  agentListPath,
  createAgent,
  fetchAgentsByIds,
} from "~/services/agents";
import { AgentCard } from "./AgentCard";

export const AGENTS_PAGE_SIZE = 50;

export const Agents = () => {
  const user = useUser();
  const canManage = useCanManageAIAgents();
  const canAdminister = useCanAdministerAIAgents();
  const [preferences, updatePreferences] = useUserPreferences();
  // The views a caller's permissions can exercise, in display order: everyone
  // can use, only manage/admin can edit, only admins get the bypass. A pure user
  // is left with one option, so the filter hides itself.
  const viewOptions = useMemo(
    () => [
      AgentListView.USABLE,
      ...(canManage || canAdminister ? [AgentListView.EDITABLE] : []),
      ...(canAdminister ? [AgentListView.ALL] : []),
    ],
    [canManage, canAdminister]
  );
  // Until `/me` resolves the capability hooks report false, so clamping the
  // saved view now would fetch `usable` for a frame on hard refresh. Trust a
  // well-formed stored view while permissions load (the backend enforces the
  // real ceiling), then clamp to what the caller can reach.
  const storedView = preferences.agentListView ?? AgentListView.USABLE;
  const reachable = user ? viewOptions : Object.values(AgentListView);
  const view = reachable.includes(storedView) ? storedView : AgentListView.USABLE;

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const [isCreating, setIsCreating] = useState(false);
  // Creating is manager-tier, matching the backend: a new agent is born DISABLED
  // and only a manager can configure and enable it.
  const canCreateAgent = canManage || canAdminister;
  const navigate = useNavigate();
  const { showError } = useToast();
  const [viewMode, setViewMode] = useViewMode("nextgen-view-mode");

  // The `view` rides in the list path, so switching it swaps the SWR key and
  // `useServerList` resets paging to the first page.
  const browse = useServerList<Agent>({
    path: agentListPath({ view }),
    search: debouncedSearch,
    pageSize: AGENTS_PAGE_SIZE,
  });
  const { pinned, isFavorite, toggleFavorite } = usePinnedList<Agent>({
    favoritesKey: AGENT_FAVORITES_KEY,
    fetchByIds: fetchAgentsByIds,
    getId: (a) => a.id,
  });

  const handleCreateAgent = useCallback(async () => {
    setIsCreating(true);
    try {
      const agent = await createAgent();
      navigate(`${ROUTE_NEXTGEN_VIEW}/agents/${agent.id}`);
    } catch (err) {
      console.error(err);
      showError("Failed to create agent");
    } finally {
      setIsCreating(false);
    }
  }, [navigate, showError]);

  const headerActions = useMemo(
    () =>
      canCreateAgent
        ? ([
            <Button
              key="new-agent"
              size="sm"
              className="text-xs"
              onClick={handleCreateAgent}
              loading={isCreating}
            >
              <Plus aria-hidden="true" />
              New Agent
            </Button>,
          ] as [ReactNode])
        : ([] as []),
    [canCreateAgent, handleCreateAgent, isCreating]
  );

  useDocumentTitle("Agents");
  useNextGenHeader({ actions: headerActions });
  usePageContext({ page: "agents" });

  const searching = debouncedSearch.trim().length > 0;
  // Favorites rise above the browse list on every view (but not while searching —
  // a matching favorite still surfaces in the results themselves). The pinned set
  // resolves independently of the view slice, so on `editable` keep only the pins
  // the caller can actually edit; a use-only favorite would otherwise sit atop a
  // tab that means "agents you can edit". `usable` and the admin `all` view are
  // supersets of the pinned set, so they show it whole.
  const pinnedForView =
    view === AgentListView.EDITABLE
      ? pinned.filter(
          (a) =>
            a.access_level === AgentAccessLevel.OWNER || a.access_level === AgentAccessLevel.EDITOR
        )
      : pinned;
  const pinnedIdsForView = new Set(pinnedForView.map((a) => a.id));
  const showPinned = !searching && pinnedForView.length > 0;
  // Drop pinned agents from the browse body so they render once, in Favorites.
  const browseItems = showPinned
    ? browse.items.filter((a) => !pinnedIdsForView.has(a.id))
    : browse.items;

  // Empty when nothing will render: no browse results and no pinned section
  // showing (favorites are hidden while searching, so a no-match search with
  // favorites still counts as empty).
  const isEmpty = !browse.isLoading && !browse.error && !showPinned && browse.items.length === 0;

  // A first-page failure (nothing loaded) takes over the whole view; a later
  // load-more failure keeps the loaded pages and offers a retry instead.
  const showLoadError = !browse.isLoading && browse.error != null && browse.items.length === 0;
  const loadMoreFailed = !browse.isLoading && browse.error != null && browse.items.length > 0;

  const renderAgents = (items: Agent[]) =>
    viewMode === "card" ? (
      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" role="list">
        {items.map((agent) => (
          <li key={agent.id} className="flex flex-col">
            <AgentCard
              agent={agent}
              view="card"
              favorite={isFavorite(agent.id)}
              onToggleFavorite={() => toggleFavorite(agent.id)}
            />
          </li>
        ))}
      </ul>
    ) : (
      <DividedList
        items={items}
        renderItem={(agent) => (
          <AgentCard
            agent={agent}
            view="list"
            favorite={isFavorite(agent.id)}
            onToggleFavorite={() => toggleFavorite(agent.id)}
          />
        )}
      />
    );

  return (
    <div
      className="mx-auto flex w-full flex-col gap-6 px-4 py-4"
      style={{ maxWidth: NEXTGEN_MAX_CONTENT_WIDTH }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="relative w-full max-w-lg">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            placeholder="Search agents..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search agents"
            className="h-8 pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <AgentViewFilter
            value={view}
            options={viewOptions}
            onChange={(next) => updatePreferences({ agentListView: next })}
          />
          <ViewToggle value={viewMode} onChange={setViewMode} />
        </div>
      </div>

      {view === AgentListView.ALL && (
        <Alert variant="warning" data-testid="agent-admin-banner">
          <ShieldAlert />
          <AlertTitle>Admin view</AlertTitle>
          <AlertDescription>
            <p>
              You can see and manage every agent in the workspace — including private ones you
              don&apos;t own. You&apos;re acting as an admin here, so tread carefully.
            </p>
          </AlertDescription>
        </Alert>
      )}

      {browse.isLoading && viewMode === "list" && (
        <ListLoadingSkeleton
          aria-label="Loading agents"
          showAvatar
          columns={[{ width: "w-40", height: "h-5", rounded: true }, { width: "w-36" }]}
        />
      )}
      {browse.isLoading && viewMode === "card" && (
        <div
          role="status"
          aria-label="Loading agents"
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl border border-border bg-muted" />
          ))}
        </div>
      )}

      {showLoadError && (
        <ListLoadError error={browse.error} resourceLabel="agents" className="py-16" />
      )}

      {isEmpty && (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <Bot className="size-8 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm font-medium text-foreground">
            {searching
              ? "No agents match your search"
              : view === AgentListView.EDITABLE
                ? "No agents you own or edit"
                : "No agents found"}
          </p>
          {searching && (
            <p className="text-xs text-muted-foreground">Try a different search term</p>
          )}
        </div>
      )}

      {!browse.isLoading && !isEmpty && !showLoadError && (
        <div data-testid="nextgen-agent-list" className="flex flex-col gap-6">
          {showPinned && (
            <>
              <section className="flex flex-col gap-3">
                <h2 className="px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Favorites
                </h2>
                {renderAgents(pinnedForView)}
              </section>
              {browseItems.length > 0 && <Separator />}
            </>
          )}
          {browseItems.length > 0 && renderAgents(browseItems)}
        </div>
      )}

      {loadMoreFailed && (
        <div className="flex flex-col items-center gap-2">
          <p className="text-xs text-destructive">Couldn&apos;t load more agents.</p>
          <Button variant="outline" size="sm" onClick={browse.retry}>
            Try again
          </Button>
        </div>
      )}

      {!browse.isLoading && !browse.error && browse.hasMore && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={browse.loadMore}
            loading={browse.isLoadingMore}
          >
            Load more ({browse.items.length} of {browse.total})
          </Button>
        </div>
      )}
    </div>
  );
};
